import React from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import InputBase from '@mui/material/InputBase';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import axios from 'src/axios';
import { isGeneId, isPhenotypeId } from 'src/util/util';
import {
  readTerminologyAvailability,
  terminologyAvailabilityFromError
} from 'src/util/terminologyAvailability';
import { CONDITION_STATUS } from './generatePedigree';
import { CHROME, OTHER_FILL } from './tokens';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';

export const DISEASE_SEARCH_URL = '/terminology/network/search/disease';
const MIN_QUERY = 3;
const DEBOUNCE_MS = 300;
const RESULT_LIMIT = 12;
const OTHER_LABEL = 'Other Disease/Comment';

function Swatch({ status }) {
  const filled = status !== CONDITION_STATUS.unchecked;
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 8,
        height: 8,
        flex: 'none',
        borderRadius: '1.5px',
        border: `1px solid ${
          status === CONDITION_STATUS.other ? '#a8a8a8' : CHROME.text
        }`,
        backgroundColor: !filled
          ? 'transparent'
          : status === CONDITION_STATUS.other
          ? OTHER_FILL
          : CHROME.text
      }}
    />
  );
}

function chipTitle({ status, choice, codedName, nearest }) {
  if (choice?.other)
    return translate('You chose {{otherLabel}}. Click to change.', {
      otherLabel: translate(OTHER_LABEL)
    });
  if (choice)
    return translate('You chose {{name}} ({{id}}). Click to change.', {
      name: choice.name,
      id: choice.id
    });
  if (status === CONDITION_STATUS.coded)
    return translate('Coded as {{codedName}}. Click to change.', {
      codedName: codedName || translate('an exact disease-name match')
    });
  if (status === CONDITION_STATUS.other)
    return nearest
      ? translate(
          'Recorded as {{otherLabel}} with this wording in the comment. The closest match was "{{nearest}}". Click to code it.',
          { otherLabel: translate(OTHER_LABEL), nearest }
        )
      : translate(
          'Recorded as {{otherLabel}} with this wording in the comment. Click to code it.',
          { otherLabel: translate(OTHER_LABEL) }
        );
  return translate(
    'Not checked against the terminology service. Click to code it.'
  );
}

export function ConditionChip({
  term,
  status,
  choice,
  codedName,
  nearest,
  accent,
  onOpen
}) {
  const isOther = status === CONDITION_STATUS.other;
  const suffix =
    choice && !choice.other ? choice.name : isOther ? OTHER_LABEL : null;
  return (
    <Box
      component="button"
      type="button"
      onClick={(e) => onOpen(e.currentTarget, term)}
      data-condition={term}
      data-coded={status === CONDITION_STATUS.coded ? '' : undefined}
      data-uncoded={isOther ? '' : undefined}
      data-chosen={choice ? '' : undefined}
      title={chipTitle({ status, choice, codedName, nearest })}
      aria-haspopup="dialog"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        maxWidth: '100%',
        fontFamily: 'inherit',
        fontSize: '0.7rem',
        lineHeight: 1.4,
        color: isOther ? CHROME.muted : CHROME.text,
        backgroundColor: 'transparent',
        cursor: 'pointer',
        border: choice
          ? `1px solid ${accent}`
          : isOther
          ? `1px dashed ${CHROME.muted}`
          : `1px solid ${CHROME.border}`,
        borderRadius: 999,
        px: 0.85,
        py: 0.15,
        transition: 'background-color 120ms ease',
        '&:hover': { backgroundColor: CHROME.header },
        '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 1 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
      }}
    >
      <Swatch status={status} />
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {term}
      </Box>
      {suffix && (
        <Box
          component="span"
          sx={{
            fontSize: '0.62rem',
            color: CHROME.muted,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 180
          }}
        >
          · {suffix}
        </Box>
      )}
    </Box>
  );
}

// Diseases only: a phenotype or gene would not be a disease on this record.
const toDiseaseRows = (data) =>
  data?.fellBackToHpo
    ? []
    : (data?.results || [])
        .filter((r) => r?.id && !isPhenotypeId(r.id) && !isGeneId(r.id))
        .map((r) => ({
          id: r.id,
          name: r.name,
          source: r.source || String(r.id).split(':')[0]
        }));

function useDiseaseSearch(query) {
  const [state, setState] = React.useState({
    rows: [],
    loading: false,
    unavailable: null
  });

  React.useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      setState({ rows: [], loading: false, unavailable: null });
      return undefined;
    }
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    const timer = window.setTimeout(() => {
      axios
        .get(DISEASE_SEARCH_URL, { params: { q, limit: RESULT_LIMIT } })
        .then(({ data }) => {
          if (!live) return;
          setState({
            rows: toDiseaseRows(data),
            loading: false,
            unavailable: readTerminologyAvailability(data)
          });
        })
        .catch(() => {
          if (!live) return;
          setState({
            rows: [],
            loading: false,
            unavailable: terminologyAvailabilityFromError()
          });
        });
    }, DEBOUNCE_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  return state;
}

const rowSx = {
  fontSize: '0.74rem',
  minHeight: 0,
  py: 0.7,
  px: 1.25,
  gap: 1,
  alignItems: 'flex-start',
  whiteSpace: 'normal'
};

export function ConditionSearch({
  anchorEl,
  term,
  status,
  choice,
  accent,
  onChoose,
  onClose
}) {
  const t = useAutoTranslation();

  const [query, setQuery] = React.useState(term || '');
  const listRef = React.useRef(null);
  const { rows, loading, unavailable } = useDiseaseSearch(query);

  React.useEffect(() => setQuery(term || ''), [term]);

  const pick = (value) => {
    onChoose(term, value);
    onClose();
  };

  const onFieldKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      listRef.current?.querySelector('[role="menuitem"]')?.focus();
    }
  };

  const keptOther =
    choice?.other || (!choice && status === CONDITION_STATUS.other);
  const q = query.trim();

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      // Stops Escape reaching the panel's window listener.
      onKeyDown={(e) => {
        if (e.key === 'Escape') e.stopPropagation();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-label': t('Code "{{term}}"', { term }),
          sx: {
            mt: 0.5,
            width: 340,
            maxWidth: 'calc(100vw - 32px)',
            border: `1px solid ${CHROME.border}`,
            borderRadius: 1.5,
            boxShadow: '0 8px 24px rgba(15, 30, 38, 0.16)'
          }
        }
      }}
    >
      <Box sx={{ px: 1.25, pt: 1.1, pb: 0.9 }}>
        <Box sx={{ fontSize: '0.7rem', color: CHROME.muted, mb: 0.6 }}>
          {t('The history says “')}
          {term}&rdquo;
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            border: `1px solid ${CHROME.border}`,
            borderRadius: 1,
            '&:focus-within': { borderColor: accent }
          }}
        >
          <SearchIcon sx={{ fontSize: 15, color: CHROME.muted }} />
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onFieldKeyDown}
            onFocus={(e) => e.target.select()}
            autoFocus
            fullWidth
            placeholder={t('Search diseases')}
            inputProps={{ 'aria-label': t('Search diseases') }}
            sx={{ fontSize: '0.78rem', py: 0.4 }}
          />
          {loading && (
            <CircularProgress size={12} sx={{ color: CHROME.muted }} />
          )}
        </Box>
      </Box>

      <MenuList
        ref={listRef}
        dense
        sx={{
          py: 0,
          maxHeight: 280,
          overflowY: 'auto',
          borderTop: `1px solid ${CHROME.hairline}`
        }}
      >
        <MenuItem
          onClick={() => pick({ other: true })}
          selected={keptOther}
          data-choice="other"
          sx={{ ...rowSx, alignItems: 'center' }}
        >
          <Swatch status={CONDITION_STATUS.other} />
          <Box sx={{ flex: 1 }}>
            {t('Keep as')} {t(OTHER_LABEL)}
            <Box sx={{ fontSize: '0.64rem', color: CHROME.muted }}>
              {t('The wording goes into the diagnosis comment')}
            </Box>
          </Box>
          {keptOther && <CheckIcon sx={{ fontSize: 14, color: accent }} />}
        </MenuItem>

        {rows.map((row) => {
          const chosen = choice?.id === row.id;
          return (
            <MenuItem
              key={row.id}
              onClick={() => pick({ id: row.id, name: row.name })}
              selected={chosen}
              data-choice={row.id}
              sx={rowSx}
            >
              <Box sx={{ flex: 1, color: CHROME.text }}>{row.name}</Box>
              <Box
                component="span"
                sx={{
                  fontSize: '0.62rem',
                  color: CHROME.muted,
                  whiteSpace: 'nowrap',
                  pt: '2px'
                }}
              >
                {row.id}
              </Box>
              {chosen && <CheckIcon sx={{ fontSize: 14, color: accent }} />}
            </MenuItem>
          );
        })}
      </MenuList>

      <Box
        sx={{
          px: 1.25,
          py: 0.8,
          fontSize: '0.66rem',
          color: CHROME.muted,
          lineHeight: 1.45,
          borderTop: `1px solid ${CHROME.hairline}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1
        }}
      >
        <span>
          {unavailable
            ? unavailable.message
            : q.length < MIN_QUERY
            ? t('Type at least {{minQuery}} characters to search.', {
                minQuery: MIN_QUERY
              })
            : loading
            ? t('Searching…')
            : rows.length
            ? t('Only diseases are listed.')
            : t('No disease matched. Try other words.')}
        </span>
        {choice && (
          <Box
            component="button"
            type="button"
            onClick={() => pick(null)}
            sx={{
              flex: 'none',
              border: 0,
              p: 0,
              background: 'none',
              fontFamily: 'inherit',
              fontSize: '0.68rem',
              fontWeight: 600,
              color: accent,
              cursor: 'pointer',
              '&:focus-visible': {
                outline: `2px solid ${accent}`,
                outlineOffset: 2
              }
            }}
          >
            {t('Use automatic result')}
          </Box>
        )}
      </Box>
    </Popover>
  );
}
